import { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from "@akuna/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScanBarcode, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface BarcodeScannerProps {
  onScan: (barcode: string) => boolean | void | Promise<boolean | void>;
  buttonVariant?: 'default' | 'outline' | 'ghost';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
}

export const BarcodeScanner = ({ onScan, buttonVariant = 'outline', buttonSize = 'default' }: BarcodeScannerProps) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<string>('barcode-scanner-container');
  const isOpenRef = useRef(isOpen);
  const stopRequestedRef = useRef(false);
  const isScanningRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const getCameraErrorMessage = useCallback((error: unknown) => {
    if (error && typeof error === 'object' && 'name' in error) {
      const errorName = String((error as { name?: string }).name);
      switch (errorName) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          return t("barcodeScanner.errors.permissionDenied");
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          return t("barcodeScanner.errors.cameraNotFound");
        case 'NotReadableError':
        case 'TrackStartError':
          return t("barcodeScanner.errors.cameraBusy");
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return t("barcodeScanner.errors.startFailed");
  }, [t]);

  const ensureCameraAccess = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      const unsupportedError = new Error(t("barcodeScanner.errors.unsupportedBrowser"));
      unsupportedError.name = 'UnsupportedBrowserError';
      throw unsupportedError;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
    });

    stream.getTracks().forEach((track) => track.stop());
  }, [t]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isScanningRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
        setIsScanning(false);
        isScanningRef.current = false;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  }, []);

  const startScanner = useCallback(async () => {
    try {
      await ensureCameraAccess();

      if (!isOpenRef.current) {
        return;
      }

      setIsScanning(true);
      isScanningRef.current = true;
      stopRequestedRef.current = false;

      const html5QrCode = new Html5Qrcode(scannerContainerRef.current);
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
        },
        async (decodedText) => {
          if (stopRequestedRef.current) {
            return;
          }
          const shouldClose = await Promise.resolve(onScan(decodedText));
          if (shouldClose === false) {
            return;
          }
          stopRequestedRef.current = true;
          await stopScanner();
          setIsOpen(false);
          toast.success(t("barcodeScanner.success"));
        },
        (error) => {
          // Ignore scanning errors, they happen frequently
          console.debug('Scanning...', error);
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      if (scannerRef.current) {
        try {
          await scannerRef.current.stop();
        } catch (stopError) {
          console.error('Error stopping scanner after failure:', stopError);
        }
        scannerRef.current.clear();
        scannerRef.current = null;
      }
      toast.error(getCameraErrorMessage(err));
      setIsScanning(false);
      setIsOpen(false);
    }
  }, [ensureCameraAccess, getCameraErrorMessage, onScan, stopScanner, t]);

  const handleDialogChange = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      stopScanner();
    }
    setIsOpen(nextOpenState);
  };

  useEffect(() => {
    if (isOpen && !isScanning) {
      startScanner();
    }
    return () => {
      stopScanner();
    };
  }, [isOpen, isScanning, startScanner, stopScanner]);

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setIsOpen(true)}
        title={t("barcodeScanner.open")}
      >
        <ScanBarcode className="h-4 w-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("barcodeScanner.title")}</DialogTitle>
            <DialogDescription>
              {t("barcodeScanner.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              id={scannerContainerRef.current}
              className="w-full aspect-video bg-muted rounded-lg overflow-hidden"
            />

            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleDialogChange(false)}
            >
              <X className="h-4 w-4 mr-2" />
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
